import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { User, Leaf, Stethoscope } from "lucide-react";

interface UserDetailDialogProps {
  userId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function UserDetailDialog({
  userId,
  open,
  onOpenChange,
}: UserDetailDialogProps) {
  const { data: profile, isLoading: profileLoading } = useQuery({
    queryKey: ["admin-user-profile", userId],
    queryFn: async () => {
      if (!userId) return null;
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("user_id", userId)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!userId && open,
  });

  const { data: plants, isLoading: plantsLoading } = useQuery({
    queryKey: ["admin-user-plants", userId],
    queryFn: async () => {
      if (!userId) return [];
      const { data, error } = await supabase
        .from("plant_identifications")
        .select("*")
        .eq("user_id", userId)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
    enabled: !!userId && open,
  });

  const { data: diagnostics, isLoading: diagnosticsLoading } = useQuery({
    queryKey: ["admin-user-diagnostics", userId],
    queryFn: async () => {
      if (!userId) return [];
      const { data, error } = await supabase
        .from("plant_diagnostics")
        .select("*, plant_identifications(popular_name)")
        .eq("user_id", userId)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
    enabled: !!userId && open,
  });

  const { data: subscription } = useQuery({
    queryKey: ["admin-user-subscription", userId],
    queryFn: async () => {
      if (!userId) return null;
      const { data, error } = await supabase
        .from("subscriptions")
        .select("*")
        .eq("user_id", userId)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!userId && open,
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Detalhes do Usuário
          </DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="profile" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="profile">Perfil</TabsTrigger>
            <TabsTrigger value="plants">
              Plantas ({plants?.length || 0})
            </TabsTrigger>
            <TabsTrigger value="diagnostics">
              Diagnósticos ({diagnostics?.length || 0})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="profile" className="space-y-4 mt-4">
            {profileLoading ? (
              <div className="space-y-3">
                <Skeleton className="h-4 w-48" />
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-4 w-40" />
              </div>
            ) : profile ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Nome</p>
                    <p className="font-medium">
                      {profile.display_name || "Não informado"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Telefone</p>
                    <p className="font-medium">{profile.phone || "Não informado"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Cadastro</p>
                    <p className="font-medium">
                      {format(new Date(profile.created_at), "dd/MM/yyyy 'às' HH:mm", {
                        locale: ptBR,
                      })}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Plano</p>
                    <Badge
                      variant={
                        subscription?.plan_type === "premium"
                          ? "default"
                          : "secondary"
                      }
                    >
                      {subscription?.plan_type === "premium" ? "Premium" : "Free"}
                    </Badge>
                  </div>
                </div>
              </div>
            ) : null}
          </TabsContent>

          <TabsContent value="plants" className="mt-4">
            <ScrollArea className="h-[400px] pr-4">
              {plantsLoading ? (
                <div className="space-y-3">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <Skeleton key={i} className="h-20 w-full" />
                  ))}
                </div>
              ) : plants?.length === 0 ? (
                <p className="text-center py-8 text-muted-foreground">
                  Nenhuma planta identificada
                </p>
              ) : (
                <div className="space-y-3">
                  {plants?.map((plant) => (
                    <div
                      key={plant.id}
                      className="flex gap-3 p-3 border rounded-lg"
                    >
                      <img
                        src={plant.image_url}
                        alt={plant.popular_name || "Planta"}
                        className="w-16 h-16 object-cover rounded-lg"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <Leaf className="h-4 w-4 text-primary" />
                          <p className="font-medium truncate">
                            {plant.popular_name || "Sem nome"}
                          </p>
                        </div>
                        <p className="text-sm text-muted-foreground italic">
                          {plant.scientific_name || ""}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {format(new Date(plant.created_at), "dd/MM/yyyy", {
                            locale: ptBR,
                          })}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </TabsContent>

          <TabsContent value="diagnostics" className="mt-4">
            <ScrollArea className="h-[400px] pr-4">
              {diagnosticsLoading ? (
                <div className="space-y-3">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <Skeleton key={i} className="h-20 w-full" />
                  ))}
                </div>
              ) : diagnostics?.length === 0 ? (
                <p className="text-center py-8 text-muted-foreground">
                  Nenhum diagnóstico realizado
                </p>
              ) : (
                <div className="space-y-3">
                  {diagnostics?.map((diagnostic) => (
                    <div
                      key={diagnostic.id}
                      className="flex gap-3 p-3 border rounded-lg"
                    >
                      <img
                        src={diagnostic.image_url}
                        alt="Diagnóstico"
                        className="w-16 h-16 object-cover rounded-lg"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <Stethoscope className="h-4 w-4 text-orange-500" />
                          <p className="font-medium truncate">
                            {(diagnostic as any).plant_identifications?.popular_name ||
                              "Planta"}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-sm">Saúde:</span>
                          <Badge
                            variant={
                              (diagnostic.health_score || 0) >= 70
                                ? "default"
                                : (diagnostic.health_score || 0) >= 40
                                ? "secondary"
                                : "destructive"
                            }
                          >
                            {diagnostic.health_score || 0}%
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          {format(new Date(diagnostic.created_at), "dd/MM/yyyy", {
                            locale: ptBR,
                          })}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
